import { useConversationReport } from "@coaching-culture/api-client";
import { ConversationReportItem, UserFilter } from "@coaching-culture/types";
import {
  Button,
  DatePill,
  Flex,
  IconWrap,
  Panel,
  PanelHeader,
  Select,
  SortableTable,
  SortableTableColumn,
  Text,
  UserFlag,
} from "@coaching-culture/ui";
import { FilterModal } from "components/FilterPanel";
import {
  addDays,
  format,
  fromUnixTime,
  getUnixTime,
  startOfDay,
} from "date-fns";
import { uniq } from "lodash";
import { unparse } from "papaparse";
import { useMemo, useState } from "react";
import { FaCommentDots } from "react-icons/fa";
import { downloadContent } from "utils";

export const ConversationsReport = () => {
  const [typeFilter, setTypeFilter] = useState("");
  const [filter, setFilter] = useState<UserFilter>({
    groups: [],
    userFields: [],
  });
  const [timeframe, setTimeframe] = useState(30);

  const time = getUnixTime(startOfDay(addDays(new Date(), -timeframe)));

  const [showFilters, setShowFilters] = useState(false);

  const onCancel = (result: UserFilter | null) => {
    if (result) {
      setFilter(result);
    }

    setShowFilters(false);
  };

  const report = useConversationReport(filter);

  const data = (report.data ?? [])
    .filter((x) => x.scheduledDate > time)
    .filter((x) => typeFilter === "" || x.conversationType === typeFilter);

  const cols = useMemo<SortableTableColumn<ConversationReportItem>[]>(
    () => [
      {
        label: "Manager",
        name: "manager",
        format: (x) => <UserFlag user={x.manager} />,
        exportFormat: (x) => x.manager.email,
        sortFormat: (x) => x.manager.name,
      },
      {
        label: "Direct Report",
        name: "directReport",
        format: (x) => <UserFlag user={x.directReport} />,
        exportFormat: (x) => x.directReport.email,
        sortFormat: (x) => x.directReport.name,
      },
      {
        label: "Type",
        name: "conversationType",
        format: (x) => x.conversationType,
      },
      {
        label: "Completed Date",
        name: "completedDate",
        exportFormat: (x) =>
          format(fromUnixTime(x.completedDate), "dd/MM/yyyy HH:mm"),
        headingProps: {
          style: { textAlign: "right" },
        },
        props: {
          style: { textAlign: "right" },
        },
        format: (x) => <DatePill value={x.completedDate} dateOnly />,
      },
    ],
    [],
  );

  function handleExport(data: Record<string, string>[]): void {
    downloadContent(
      unparse(data),
      `ConversationReport-${new Date().toLocaleDateString()}.csv`,
    );
  }

  const typeOptions = [
    { value: "", label: "(All Types)" },
    ...uniq((report.data ?? []).map((x) => x.conversationType)).map((x) => ({
      value: x,
      label: x,
    })),
  ];

  return (
    <div>
      <FilterModal
        isOpen={showFilters}
        filter={filter}
        onClose={(result) => {
          onCancel(result);
        }}
      />
      <Flex mb={3} justifyContent={"flex-end"}>
        <Button color="primary" onClick={() => setShowFilters(true)}>
          Filters ({filter.groups.length + filter.userFields.length})
        </Button>

        <Select
          ml={2}
          width={200}
          value={timeframe}
          onChange={(val) => setTimeframe(parseInt(val.target.value))}
          options={[
            { label: "Last 14 days", value: "14" },
            { label: "Last 30 days", value: "30" },
            { label: "Last 90 days", value: "90" },
            { label: "Last 180 days", value: "180" },
            { label: "Last year", value: "365" },
          ]}
        />
      </Flex>
      <Panel>
        <PanelHeader
          justifyContent={"space-between"}
          flexDirection={["column", "row"]}
          alignItems={["flex-start", "center"]}
          height="auto"
          style={{ gap: 12 }}
        >
          <Flex alignItems={"center"} flex="1">
            <IconWrap icon={FaCommentDots} color="primary" size="2em" />
            <Text fontSize={3} fontWeight={600} ml={2}>
              Completed Conversations History
            </Text>
          </Flex>
          <Select
            maxWidth={300}
            value={typeFilter}
            options={typeOptions}
            onChange={(ev) => setTypeFilter(ev.target.value)}
          />
        </PanelHeader>
        <SortableTable
          paged
          columns={cols}
          data={data}
          onExport={handleExport}
          emptyMessage={"None found"}
        />
      </Panel>
    </div>
  );
};
